import { useMemo } from 'react';
import { graphql, readInlineData } from 'react-relay';
import { useLocation } from 'react-router-dom';

import { useNftProfileOwner } from './useNftProfileOwner';

import { type NftProfileCollection_viewer$data as ViewerType } from '../components/NftProfileCollection/__generated__/NftProfileCollection_viewer.graphql';
import { type importCollectionsConfig_contractImport$key } from './__generated__/importCollectionsConfig_contractImport.graphql';

type DerivedCreatorSectionConfig = {
    isVisible: boolean;
    showImportList: boolean;
    showCreations: boolean;
    showImportCollection: boolean;
    path: string | null;
    tokenContractImportId?: string;
    hasTitleLink: boolean;
    showBackNav: boolean;
    totalResults: number;
};

export const TOKEN_CONTRACT_ID_REGEX_STRING = '\\d+-0x\\w+' as const;
export const TOKEN_CONTRACT_ID_REGEX = new RegExp(TOKEN_CONTRACT_ID_REGEX_STRING);

type ContractStatus = 'COMPLETE' | 'ERROR' | 'PROCESSING';
type ItemsStatus = Record<'complete' | 'error' | 'processing', number> & { empty: boolean };

type State = {
    contract: ContractStatus | null;
    items: ItemsStatus;
    allowPublic: boolean;
    serviceId: string;
};

export function deriveState(
    contractImportRef: importCollectionsConfig_contractImport$key | null = null
): State {
    const contractImport = readInlineData(
        graphql`
            fragment importCollectionsConfig_contractImport on NftTokenContractImport @inline {
                serviceId
                importStatus
                errored: items(importStatuses: [NFT_ITEM_IMPORT_STATUS_ERROR]) {
                    totalResults
                }
                processing: items(importStatuses: [NFT_ITEM_IMPORT_STATUS_PROCESSING]) {
                    totalResults
                }
                completed: items(importStatuses: [NFT_ITEM_IMPORT_STATUS_COMPLETE]) {
                    totalResults
                }
            }
        `,
        contractImportRef
    );
    const { completed, errored, importStatus, processing, serviceId } = contractImport || {};

    const state: State = {
        contract: null,
        serviceId: serviceId || '',
        items: {
            empty: true,
            complete: completed?.totalResults || 0,
            error: errored?.totalResults || 0,
            processing: processing?.totalResults || 0,
        },
        allowPublic: false,
    };

    if (importStatus === 'NFT_TOKEN_CONTRACT_IMPORT_STATUS_COMPLETE') {
        state.contract = 'COMPLETE';
    } else if (importStatus === 'NFT_TOKEN_CONTRACT_IMPORT_STATUS_ERROR') {
        state.contract = 'ERROR';
    } else if (importStatus === 'NFT_TOKEN_CONTRACT_IMPORT_STATUS_PROCESSING') {
        state.contract = 'PROCESSING';
    }

    if (state.items.complete || state.items.processing || state.items.error) {
        state.items.empty = false;
    }

    state.allowPublic = state.contract === 'COMPLETE' && !!state.items.complete;

    return state;
}

export function useCreatorSectionConfig(viewer: ViewerType): DerivedCreatorSectionConfig {
    const { isProfileOwner } = useNftProfileOwner();
    const { pathname } = useLocation();

    const config = useMemo(() => {
        const ids = [];
        let importItems = 0;
        let contractsWithComplete = 0;
        for (const edge of viewer.tokenImports?.edges || []) {
            const contractImport = deriveState(edge?.node);
            importItems += contractImport.items.complete;
            contractsWithComplete += contractImport.items.complete ? 1 : 0;
            if (contractImport.allowPublic || isProfileOwner) {
                ids.push(contractImport.serviceId);
            }
        }
        const dibsItems = viewer.creations?.totalResults || 0;
        const hasDibsItems = !!dibsItems;
        const hasSomeCollections =
            contractsWithComplete > 1 || (hasDibsItems && contractsWithComplete === 1);
        const isValidId = TOKEN_CONTRACT_ID_REGEX.test(pathname);
        const currentImportId = isValidId ? ids.find(arg => !!arg && pathname.includes(arg)) : null;
        const isImportIdPath = !!currentImportId;
        const defaultImportId = !hasDibsItems && ids.length === 1 ? ids[0] : undefined;

        const showImportCollection = isImportIdPath || !!defaultImportId;
        const showImportList = !isImportIdPath && hasSomeCollections;
        const showCreations = hasDibsItems && !showImportCollection;
        const showBackNav = isImportIdPath && (hasDibsItems || importItems > 1);

        return {
            isVisible: showImportCollection || showImportList || showCreations,
            showImportList,
            showCreations,
            showImportCollection,
            showBackNav,
            path: currentImportId ? `/${currentImportId}` : null,
            tokenContractImportId: currentImportId || defaultImportId,
            hasTitleLink: showImportCollection && !hasSomeCollections,
            totalResults: dibsItems + importItems,
        };
    }, [isProfileOwner, pathname, viewer.creations, viewer.tokenImports]);
    return config;
}
